import { getLastReadTimestamp } from '../services/storage/userDataStorage';
import { getSenderId } from './identityUtils';
import { shareLineText } from './shareLine';
import { formatTimePillShort } from './locationFormatters';

/**
 * Check if a connection has unread messages.
 * Compares latest message timestamp against last read timestamp,
 * excluding messages sent by the current user.
 */
export const hasUnreadMessages = (chatMessages, chatRoomId) => {
  if (!chatMessages || chatMessages.length === 0) return false;
  const lastRead = getLastReadTimestamp(chatRoomId);
  const userMessages = chatMessages.filter(
    (msg) => msg.type === 'chat' || msg.type === 'contact_share' || msg.type === 'contact'
  );
  if (userMessages.length === 0) return false;
  const latestMessage = userMessages[userMessages.length - 1];
  const latestTimestamp = latestMessage?.timestamp;
  if (!latestTimestamp) return false;
  const myId = getSenderId();
  if (!lastRead) return latestMessage.senderId !== myId;
  return new Date(latestTimestamp) > new Date(lastRead) && latestMessage.senderId !== myId;
};

/**
 * NaN-safe timestamp extraction — returns epoch ms or 0 for missing/invalid.
 * Using 0 (not '') prevents NaN from poisoning Array.sort.
 */
export const getTime = (ts) => {
  if (!ts) return 0;
  const ms = new Date(ts).getTime();
  return isNaN(ms) ? 0 : ms;
};

/**
 * Latest message from a cached message array (most recent by timestamp).
 * Lets the chat list paint correct filter/sort/preview at first paint from
 * localStorage, before the DB enrichment lands. Returns null for an empty array.
 */
export const deriveLatestMessage = (chatMessages) => {
  if (!chatMessages || chatMessages.length === 0) return null;
  return chatMessages.reduce((latest, msg) =>
    getTime(msg.timestamp) >= getTime(latest.timestamp) ? msg : latest
  );
};

/**
 * Check if a connection has any user-sent chat messages (for chat list filtering).
 */
export const hasChatMessages = (chatMessages) => {
  if (!chatMessages || chatMessages.length === 0) return false;
  return chatMessages.some((msg) => msg.type === 'chat' || msg.type === 'system');
};

/**
 * Date-divider label for the message at `index`: a formatted time pill when it
 * falls on a different day than the previous timestamped message, else null.
 * System banners COUNT as day markers — they display their own date/time, so a
 * divider right after a same-day banner would double up (visible since
 * per-meeting re-meet banners land mid-chat, f/pxp-reconnection). Banners
 * themselves never get a divider, and neither does the first message of a room
 * (the room's banner already shows the date).
 */
export const getDateDividerLabel = (messages, index) => {
  const msg = messages?.[index];
  if (!msg || msg.type === 'system' || !msg.timestamp) return null;
  const msgDate = new Date(msg.timestamp);
  let foundPrev = false;
  for (let i = index - 1; i >= 0; i--) {
    const prev = messages[i];
    if (!prev.timestamp) continue;
    foundPrev = true;
    const prevDate = new Date(prev.timestamp);
    if (
      msgDate.getFullYear() === prevDate.getFullYear() &&
      msgDate.getMonth() === prevDate.getMonth() &&
      msgDate.getDate() === prevDate.getDate()
    ) {
      return null; // Same day
    }
    break; // Different day
  }
  // No previous timestamped message — the room's banner already shows the date
  if (!foundPrev) return null;
  return formatTimePillShort(msg.timestamp);
};

/**
 * The greeting-door rule (exchange-polish, contract #9a end-state 2026-07-21):
 * on the Anon side, the only chat bubble that doors to the detail page is the
 * Profile's sheet-queued greeting — identified as the earliest received plain
 * chat message that precedes the first received share line (the greeting
 * always posts just before its share; commit order is the marker).
 * Fail-closed: no received share line → no door — a missed door is a
 * nice-to-have lost, a wrong door is a lie.
 *
 * @param {Array} sortedMessages - display messages in chronological order
 * @param {function} isSentMessage - (msg) => boolean, viewer's own messages
 * @returns {string|null} the greeting message's id, or null
 */
export const findGreetingDoorId = (sortedMessages, isSentMessage) => {
  if (!sortedMessages || sortedMessages.length === 0) return null;
  const firstShareIdx = sortedMessages.findIndex(
    (m) => m.type === 'contact_share' && m.speech && !isSentMessage(m)
  );
  if (firstShareIdx === -1) return null;
  for (let i = 0; i < firstShareIdx; i++) {
    const m = sortedMessages[i];
    const isPlainChat =
      m.type === 'chat' &&
      !m.shareType &&
      m.contentType !== 'image' &&
      !m.isInviteCta &&
      !m.isWelcomeCta &&
      !m.anonName;
    if (!isSentMessage(m) && isPlainChat) {
      return m.id || null;
    }
  }
  return null;
};

/**
 * Format phone for list preview: US +1 numbers as XXX-XXX-XXXX, international as-is.
 */
export const formatPhoneForPreview = (phone) => {
  if (!phone) return phone;
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    const digits = cleaned.slice(2);
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

/**
 * Format a latestMessage object into a preview string/element for connection cards.
 * Handles invite CTAs, images, card-style contacts, inline contacts, and text.
 *
 * @param {object|null} latestMessage - The latest message object
 * @param {string} mySenderId - Current user's sender ID (from getSenderId())
 * @returns {{ recentMessage: React.ReactNode|null, isMessageFromUser: boolean }}
 */
export const formatMessagePreview = (latestMessage, mySenderId) => {
  if (!latestMessage) return { recentMessage: null, isMessageFromUser: false };

  const isMessageFromUser = latestMessage.senderId === mySenderId;
  let recentMessage = null;

  if (latestMessage.isInviteCta) {
    recentMessage = isMessageFromUser ? <em>Sent invite</em> : <em>Received invite</em>;
  } else if (latestMessage.type === 'contact_share' && latestMessage.speech) {
    // Share lines preview as their spoken text ("My insta is brilarsen")
    recentMessage = shareLineText(latestMessage);
  } else if (latestMessage.contentType === 'image') {
    recentMessage = isMessageFromUser ? <em>Sent photo</em> : <em>Received photo</em>;
  } else if (
    (latestMessage.type === 'contact_share' || latestMessage.type === 'contact') &&
    latestMessage.cardStyle
  ) {
    recentMessage = isMessageFromUser ? <em>Sent contact</em> : <em>Received contact</em>;
  } else if (latestMessage.type === 'contact_share' || latestMessage.type === 'contact') {
    const contact = latestMessage.contacts?.[0];
    const socialTypes = ['instagram', 'twitter', 'tiktok', 'snapchat'];
    const rawValue = contact?.displayValue || contact?.value;
    let contactValue = rawValue;
    if (contact?.type === 'phone') {
      contactValue = formatPhoneForPreview(rawValue);
    } else if (socialTypes.includes(contact?.type) && rawValue && !rawValue.startsWith('@')) {
      contactValue = `@${rawValue}`;
    }
    recentMessage = contactValue;
  } else {
    recentMessage = latestMessage.message || latestMessage.content || null;
  }

  return { recentMessage, isMessageFromUser };
};

/**
 * Latest shared photo in a message list — the card-thumbnail "receipt" rule,
 * one home for both People lists (review 2026-08-09: two inline copies had
 * already diverged, and the Anon list paid an array copy + reverse per card
 * per render). Backwards index scan — no allocation. Callers pass whatever
 * messages they have loaded (the Anon list holds full history; the Profile
 * list holds only latestMessage — a known data-availability asymmetry).
 *
 * @param {Array|null|undefined} messages - chat messages, oldest → newest
 * @returns {string|null} https URL of the latest shared image, or null
 */
export const latestSharedImageUrl = (messages) => {
  if (!messages) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (
      m?.contentType === 'image' &&
      typeof m?.content === 'string' &&
      m.content.startsWith('http')
    ) {
      return m.content;
    }
  }
  return null;
};
